import React from "react";

import { ButtonView } from "common/views/button/Button.view";
import { DialogView } from "common/views/dialog/Dialog.view";
import { IMenuNode } from "common/views/menuTree/MenuTree.view";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import ILocalCacheService from "core/service/cache/ILocalCacheService";
import ServiceRegistry from "core/util/ServiceRegistry";
import { FragmentLayout } from "../../core/components/FragmentLayout";
import UserTagsPanelConfig from "./config/UserTagsPanelConfig";
import { Tag } from "./entities/Tag";
import UserTag, { UserTagAction } from "./entities/UserTag";
import * as resultsFragments from "./fragments/fragments";
import labelProvider from "./UserTags.labels";

const { Labels } = labelProvider;

export interface IUserTagsPanelState {
	changed: boolean; // if the user has selected anything new
	isOpen: boolean; // for dialog edit view
	loading: boolean;
	saving: boolean;
	selected: null | Tag[]; // saving state for changing the tags
}

interface IUserTagsPanelCache {
	dismissed: boolean;
}

const CACHE_KEY = "panel:usertags";

@Panel({id: "user-tags", configClass: UserTagsPanelConfig, labelProvider})
export default class UserTagsPanel extends React.Component<IPanelProps<UserTagsPanelConfig>, IUserTagsPanelState> {

	public state: IUserTagsPanelState = {
		changed: false,
		isOpen: false,
		loading: false,
		saving: false,
		selected: null
	};

	public render() {
		const { config } = this.props;

		const fragmentProps = {
			config,
			id: this.props.id,
			instance: this.props.instance,
			onSelectTag: this.onSelectTag,
			onSubmit: this.onSubmit,
			state: this.state
		};

		return (
			<div>
				<ButtonView viewKey="edit.button" onClick={this.toggleOpen}>
					<div>{this.renderSelected()}</div>
				</ButtonView>
				<DialogView isOpen={this.state.isOpen} onClose={this.toggleOpen} viewKey="dialog">
					<FragmentLayout layout={config.resultsLayout} fragments={resultsFragments} props={fragmentProps} />
				</DialogView>
			</div>
		);
	}

	private renderSelected = () => {
		const { selected } = this.state;
		if (!selected || selected.length < 1) {
			return <Labels.Edit />;
		}

		const { config } = this.props;

		if (config.maxVisibleSelected > 0 && selected.length > config.maxVisibleSelected) {

			return [
				...selected.slice(0, config.maxVisibleSelected).map((tag, i) => (
					<React.Fragment key={tag.id}>
						{i > 0 && (
							<span>,{' '}</span>
						)}
						<Label k={`Tag.${tag.id}.name`} v={tag.name} />
					</React.Fragment>
				)),
				<React.Fragment key='overflow'>
					<span>,{' '}</span>
					<Labels.Overflow args={{count: (selected.length - config.maxVisibleSelected).toString()}} />
				</React.Fragment>
			];
		}

		return selected.map((tag, i) => (
			<React.Fragment key={tag.id}>
				{i > 0 && (
					<span>,{' '}</span>
				)}
				<Label k={`Tag.${tag.id}.name`} v={tag.name} />
			</React.Fragment>
		));
	}

	private onSelectTag = (node: IMenuNode<Tag>) => {
		const sTag = node.data;

		let wasRemoved = false;

		const nextSelected = (this.state.selected || []).filter((tag) => {
			if (tag.id === sTag.id) {
				wasRemoved = true;
				return false;
			}
			return true;
		});

		if (!wasRemoved) {
			nextSelected.push(sTag);
		}

		this.setState({changed: true, selected: nextSelected});
	}

	private onSubmit = () => {
		const { config } = this.props;
		const { changed, selected } = this.state;

		// cache that the user has disimissed this once
		ServiceRegistry.get(ILocalCacheService).put(CACHE_KEY, {dismissed: true});

		if (changed) {

			// TODO should this be create_many or user_update mutation with tagIds field?
			this.setState({saving: true}, () => {
				const action = {
					id: 0,
					tagIds: selected.map((tag) => tag.id),
					clearExistingFromTagSetIds: config.tagSets || null
				};

				ServiceRegistry.getMutationService().create(action, UserTagAction).then((res) => {

					// refresh queries?

					this.setState({
						changed: false,
						saving: false,
						isOpen: false
					}, () => this.props.actions.forceQueryRefresh());
				}).catch(() => {
					this.setState({
						changed: false,
						saving: false,
						isOpen: false
					});
				});
			});
		} else {
			this.setState({isOpen: false});
		}
	}

	public componentDidMount() {
		const { config } = this.props;

		const prompt: boolean = !!config.autoPrompt && !ServiceRegistry.get(ILocalCacheService).get<IUserTagsPanelCache>(CACHE_KEY, {dismissed: false}).dismissed;

		this.setState({loading: true}, () => {

			ServiceRegistry.getQueryService().findMany(UserTag, {tagSetIds: config.tagSets || null}).then((res: any) => {

				const selected = res.data.entities && res.data.entities.map((ut: UserTag) => ut.tag) || [];

				this.setState({
					isOpen: prompt && selected.length < 1,
					loading: false,
					selected
				});

			}).catch(() => {

				this.setState({
					isOpen: prompt,
					loading: false,
					selected: []
				});

			});
		});
	}

	private toggleOpen = () => this.setState({isOpen: !this.state.isOpen});
}



// WEBPACK FOOTER //
// ./src/panels/tags/UserTags.panel.tsx